import { isMatch, getMatchScores, getFormattedTitle, isTeamSport, isPPV, getEventStatus, EVENT_STATUS } from '@shared/utils/eventUtils';
import { getDateTime, getRelativeTime } from '@shared/utils/timeUtils';
import { CategoryChild } from '@shared/enums/CategoryChild';
import { CategoryParent } from '@shared/enums/CategoryParent';
import { ExternalPlatform } from '@shared/enums/ExternalPlatform';
import { EventType } from '@shared/enums/EventType';

/**
 *
 * @param {*} event
 * @returns {boolean}
 */
export const isLeagueMatch = (event) => {
	return event.categoryParent === CategoryParent.MATCH && event.categoryChild === CategoryChild.MATCH_LEAGUE;
};

/**
 *
 * @param {*} event
 * @return {?string} team logo - returns logo of first participant
 */
export const getTeamLogo = (event) => {
	return event.participants?.[0]?.participatable?.imageUrl ?? null;
};

const getLeagueLogo = (event) => {
	if (!isLeagueMatch(event)) {
		return null;
	}

	const leagueLogo = event.league?.imageUrl || null;
	return leagueLogo?.includes('background_auth') ? null : leagueLogo;
};

const getLeagueName = (event) => {
	return event.league?.name ?? null;
};

/**
 *
 * @param event
 * @returns {array}
 */
export const getTags = (event) => {
	const tags = [];

	const publishStart = event.publishStart;
	const publishEnd = event.publishEnd;
	const unixNow = Math.floor(Date.now() / 1000);

	if (publishStart) {
		const publishStartTime = new Date(publishStart).getTime() / 1000;
		if (unixNow < publishStartTime) {
			tags.push({
				type:  'publish',
				label: $I18n.trans('arena.available_from', { time: getDateTime(publishStartTime, { shortDateTime: true }) }),
			});
		}
	}

	if (publishEnd) {
		const publishEndTime = new Date(publishEnd).getTime() / 1000;
		if (unixNow < publishEndTime) {
			tags.push({
				type:  'publish',
				label: getRelativeTime(publishEndTime, { useRemainingHoursDays: true }),
			});
		}
	}

	tags.push({
		type:  'sport',
		label: $I18n.trans(`sports.${event.sport.name}.name`),
	});

	if (event.categoryParent === CategoryParent.HIGHLIGHT) {
		tags.push({
			type:  'highlight',
			label: $I18n.trans(`sports.${event.sport.name}.categories.parent.${event.categoryParent}`),
		});
	}

	const eventStatus = getEventStatus(event.type, event.startTimestamp, event.endTimestamp);
	if (eventStatus === EVENT_STATUS.REPLAY && event.type === EventType.EVENT) {
		tags.push({
			type:  'replay',
			label: $I18n.trans_choice('commons.replay', 1),
			icon:  'replay',
		});
	}

	return tags;
};

export const EVENT_TYPE = {
	MATCH_LEAGUE:    'match_league',
	MATCH_OTHER:     'match_other', // also includes training match
	TEAM_TOURNAMENT: 'team_tournament',
	TEAM_TRAINING:   'team_training',
	SINGLE:          'single',
	SINGLE_TRAINING: 'single_training',
};

export const getEventType = (event) => {
	if (isLeagueMatch(event)) {
		return EVENT_TYPE.MATCH_LEAGUE;
	}
	if (isMatch(event)) {
		return EVENT_TYPE.MATCH_OTHER;
	}
	if (
		event.categoryParent === CategoryParent.COMPETITION &&
		(
			event.categoryChild === CategoryChild.COMPETITION_ROUND_ROBIN ||
			event.categoryChild === CategoryChild.COMPETITION_SINGLE_ELIMINATION ||
			event.categoryChild === CategoryChild.COMPETITION_DOUBLE_ELIMINATION
		)
	) {
		return EVENT_TYPE.TEAM_TOURNAMENT;
	}
	if (isTeamSport(event) && event.categoryParent === CategoryParent.TRAINING) {
		return EVENT_TYPE.TEAM_TRAINING;
	}
	if (event.categoryParent === CategoryParent.TRAINING) {
		return EVENT_TYPE.SINGLE_TRAINING;
	}

	return EVENT_TYPE.SINGLE;
};

const getExternalEventPlatformIcon = (platform) => {
	switch (platform) {
	case ExternalPlatform.SRF:
		return '/images/external-platforms/srf.png';
	case ExternalPlatform.RTS:
		return '/images/external-platforms/rts.png';
	case ExternalPlatform.RSI:
		return '/images/external-platforms/rsi.png';
	case ExternalPlatform.YOUTUBE:
		return '/images/external-platforms/youtube.png';
	case ExternalPlatform.FACEBOOK:
		return '/images/external-platforms/facebook.png';
	case ExternalPlatform.TIKTOK:
		return '/images/external-platforms/tiktok.png';
	case ExternalPlatform.TWITCH:
		return '/images/external-platforms/twitch.png';
	case ExternalPlatform.VIMEO:
		return '/images/external-platforms/vimeo.png';
	case ExternalPlatform.EUROVISIONSPORT:
		return '/images/external-platforms/eurovision.png';
	case ExternalPlatform.FISU_TV:
		return '/images/external-platforms/fisu-tv.png';
	case ExternalPlatform.GYM_TV:
		return '/images/external-platforms/gym-tv.png';
	case ExternalPlatform.FIG_TV:
		return '/images/external-platforms/figtv.png';
	case ExternalPlatform.LA_TELE:
		return '/images/external-platforms/la-tele.png';
	case ExternalPlatform.FISTBALL_TV:
		return '/images/external-platforms/fistball-tv.png';
	default:
		return '/images/external-platforms/generic.png';
	}
};

// We want to redirect to the TV view if:
// - we're on BlueTV box and
// - event is not upcoming and
// - event is not PPV or event is PPV AND event is unlocked
const redirectToTvView = (event) => {
	return $Helpers.isBlueTv() &&
	getEventStatus(event.type, event.startTimestamp, event.endTimestamp) !== EVENT_STATUS.UPCOMING &&
	(!isPPV(event) || (isPPV(event) && event.isUnlocked));
};

/**
 *
 * @param {*} event
 * @param {boolean} large
 * @returns {object} All necessary props for the EventCard component
 */
export const EventCardProps = (event, _options = {}) => {
	const options = {
		large:                  false,
		flexibleSize:           false,
		isEmbed:                false,
		hideTags:               [],
		showInfos:              true,
		hidePaymentRestriction: false,
		hideLoginRestriction:   false,
		hasBookmarkedEvent:     false,
		openInNewTab:           false,
		showEmbedPaymentButton: false,
		showCountdown:          false,
		showLeagueLabel:        true,
		// feed card specific props
		isFeedCard:             false,
		sharingEnabled:         false,
		isInViewport:           false,
		bookmarkEnabled:        false,
		..._options,
	};

	const isExternalEvent = !!event.externalEventLink;
	const isPaymentRestrictedAndNotUnlocked = options.hidePaymentRestriction ? false : isPPV(event);
	const isLoginRestrictedAndNotUnlocked = options.hideLoginRestriction ? false : event.isLoginProtected;

	return {
		eventType:      getEventType(event),
		type:           event.type,
		unixStart:      event.startTimestamp,
		unixKickoff:    event.startTimestamp + (event.kickoffOffsetMinutes * 60),
		unixEnd:        event.endTimestamp,
		bgImgUrl:       event.imageUrl || null,
		bgImgType:      event.imageType || null,
		videoAvailable: !!event.videoAvailable,
		leagueLogo:     getLeagueLogo(event),
		leagueName:     getLeagueName(event),
		tags:           getTags(event),
		sportIcon:      event.sport?.imageUrl || '/images/sport-icons/calendar.svg#icon',
		link:           isExternalEvent
			? event.externalEventLink
			: redirectToTvView(event) ? event.urlTV : event.url,
		couchModeAvailable:        (isExternalEvent || options.isEmbed) ? false : event.couchmodeAvailable,
		couchModeClipsCount:       event.couchmodeTagsCount,
		audioLanguages:            event.audioLanguages || [],
		homeImgUrl:                getMatchScores(event).homeImgUrl,
		guestImgUrl:               getMatchScores(event).guestImgUrl,
		eventName:                 getFormattedTitle(event, { noLineBreaksForTeamNames: true }),
		subtitle:                  event.subtitle || null,
		isPaymentRestrictedAndNotUnlocked,
		isLoginRestrictedAndNotUnlocked,
		openInNewTab:              options.openInNewTab || isExternalEvent,
		rawEvent:                  event,
		placeholderText:           event.placeholder ? JSON.parse(event.placeholderText) : null,
		isExternalEvent,
		externalEventPlatformIcon: getExternalEventPlatformIcon(event.externalEventPlatformName),
		videoCroppingEnabled:      event?.downloadable && event?.arenaCroppingEnabled,
		videoDownloadEnabled:      event?.downloadable && !isPaymentRestrictedAndNotUnlocked && (window.$arenaConfig.videoDownloadEnabled || event?.arenaDownloadEnabled),
		// Placement specific props
		large:                     options.large,
		flexibleSize:              options.flexibleSize,
		isEmbed:                   options.isEmbed,
		hideTags:                  options.hideTags,
		showInfos:                 options.showInfos,
		showLeagueLabel:           options.showLeagueLabel,
		showEmbedPaymentButton:    options.showEmbedPaymentButton,
		showCountdown:             options.showCountdown,
		isBookmarked:              options.hasBookmarkedEvent,
		isFeedCard:                options.isFeedCard,
		sharingEnabled:            options.isFeedCard && options.sharingEnabled,
		isInViewport:              options.isInViewport,
		bookmarkEnabled:           options.isFeedCard && options.bookmarkEnabled,
	};
};
